import { useQueryClient } from '@tanstack/react-query';
import { financingEndpoints } from '@/lib/api/endpoints';
import { apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import type {
  ApplyInvoiceFinancingPaymentInput,
  FinancingApplication,
  FinancingEntityType,
  UpsertFinancingInput,
} from '@/types/financing';
import { invoiceKeys, jobKeys, receiptKeys } from '@/lib/queryKeys';

const key = (entityType: FinancingEntityType, entityId: string) =>
  ['financing', entityType, entityId] as const;

export function useFinancing(entityType: FinancingEntityType, entityId: string, enabled = true) {
  return useApiQuery<FinancingApplication | null>({
    queryKey: key(entityType, entityId),
    queryFn: () =>
      apiGet<FinancingApplication | null>(
        operationsClient,
        financingEndpoints.detail(entityType, entityId),
      ),
    enabled: enabled && Boolean(entityId),
  });
}

export function useUpsertFinancing(entityType: FinancingEntityType, entityId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<FinancingApplication, UpsertFinancingInput>({
    mutationFn: (input) =>
      apiPut<FinancingApplication, UpsertFinancingInput>(
        operationsClient,
        financingEndpoints.detail(entityType, entityId),
        input,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(key(entityType, entityId), data);
      void queryClient.invalidateQueries({ queryKey: ['financing'] });
    },
  });
}

export function useApplyInvoiceFinancingPayment(invoiceId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<FinancingApplication, ApplyInvoiceFinancingPaymentInput>({
    mutationFn: (input) =>
      apiPost<FinancingApplication, typeof input>(
        operationsClient,
        financingEndpoints.applyInvoicePayment(invoiceId),
        input,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(key('invoice', invoiceId), data);
      void queryClient.invalidateQueries({ queryKey: ['financing'] });
      void queryClient.invalidateQueries({ queryKey: receiptKeys.all() });
      if (data.invoiceId) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(data.invoiceId) });
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
        void queryClient.invalidateQueries({
          queryKey: [...invoiceKeys.detail(data.invoiceId), 'payments'],
        });
      }
      if (data.jobId) {
        void queryClient.invalidateQueries({ queryKey: jobKeys.payments(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(data.jobId) });
      }
    },
  });
}
